// File: components/actions/BottomBar.js

import React from 'react';
import { Star, Share2, FileText, StickyNote } from 'lucide-react';
import {
    isBookmarked,
    addBookmark,
    removeBookmark,
    getNote
} from '../../utils/storage';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * BottomBar Component
 * 
 * Bottom action bar with save, share, PDF export, and note actions.
 * Mobile-first design with 60px height and touch-friendly buttons.
 * Sticky positioning at bottom of viewport.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.section - Current section data
 * @param {function} props.onSave - Handler for save/bookmark action
 * @param {function} props.onShare - Handler for share action
 * @param {function} props.onPDF - Handler for PDF export action
 * @param {function} props.onNote - Handler for note action
 * @param {boolean} props.showSave - Show save button
 * @param {boolean} props.showShare - Show share button
 * @param {boolean} props.showPDF - Show PDF button
 * @param {boolean} props.showNote - Show note button
 * @param {string} props.className - Additional CSS classes
 */
const BottomBar = ({
    section = null,
    onSave,
    onShare,
    onPDF,
    onNote,
    showSave = true,
    showShare = true,
    showPDF = true,
    showNote = true,
    className = ''
}) => {
    const { t } = useLanguage();
    const [saved, setSaved] = React.useState(false);
    const [hasNote, setHasNote] = React.useState(false);

    // Check if section is bookmarked and has note
    React.useEffect(() => {
        if (!section) return;

        const bookmarked = isBookmarked(section.code);
        setSaved(bookmarked);

        const note = getNote(section.code);
        setHasNote(!!note);
    }, [section]);

    // Handle save/bookmark
    const handleSave = () => {
        if (!section) return;

        if (saved) {
            removeBookmark(section.code);
            setSaved(false);
        } else {
            addBookmark({
                code: section.code,
                title: section.title,
                regulationCode: section.regulationCode,
                subpart: section.subpart
            });
            setSaved(true);
        }

        if (onSave) {
            onSave(!saved);
        }
    };

    // Handle share
    const handleShare = async () => {
        if (!section) return;

        // Try native share API first
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `${section.code} - ${section.title}`,
                    text: `OSHA Regulation: ${section.code} - ${section.title}`,
                    url: window.location.href
                });
                return;
            } catch (error) {
                // User cancelled or error - fall through to custom handler
                if (error.name !== 'AbortError') {
                    console.error('Share failed:', error);
                }
            }
        }

        // Fall back to custom share handler
        if (onShare) {
            onShare(section);
        }
    };

    // Handle PDF export
    const handlePDF = () => {
        if (!section) return;

        if (onPDF) {
            onPDF(section);
        }
    };

    // Handle note
    const handleNote = () => {
        if (!section) return;

        if (onNote) {
            onNote(section);
        }
    };

    // Don't render if no section
    if (!section) {
        return null;
    }

    return (
        <div className={`bottom-bar ${className}`}>
            {showSave && (
                <button
                    className={`bottom-bar-item ${saved ? 'active' : ''}`}
                    onClick={handleSave}
                    aria-label={saved ? 'Remove bookmark' : 'Save bookmark'}
                    type="button"
                >
                    <Star size={20} fill={saved ? 'currentColor' : 'none'} />
                    <span className="bottom-bar-label">
                        {saved ? t('actions.saved') : t('actions.save')}
                    </span>
                </button>
            )}

            {showShare && (
                <button
                    className="bottom-bar-item"
                    onClick={handleShare}
                    aria-label="Share section"
                    type="button"
                >
                    <Share2 size={20} />
                    <span className="bottom-bar-label">{t('actions.share')}</span>
                </button>
            )}

            {showPDF && (
                <button
                    className="bottom-bar-item"
                    onClick={handlePDF}
                    aria-label="Export to PDF"
                    type="button"
                >
                    <FileText size={20} />
                    <span className="bottom-bar-label">{t('actions.pdf')}</span>
                </button>
            )}

            {showNote && (
                <button
                    className={`bottom-bar-item ${hasNote ? 'active' : ''}`}
                    onClick={handleNote}
                    aria-label={hasNote ? 'View note' : 'Add note'}
                    type="button"
                >
                    <StickyNote size={20} />
                    <span className="bottom-bar-label">{t('actions.note')}</span>
                </button>
            )}
        </div>
    );
};

export default BottomBar;
